import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, Route, Routes, useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import {URL_LENGTH_LIMIT} from 'src/hooks/useFilters';
import {getUser} from 'src/modules/admin/services';
import {AttributesController} from 'src/modules/attributes/attributes.controller';
import {clearAttributesFilters} from 'src/modules/attributes/services/filters/actions';
import {getFilteredAttributesLength} from 'src/modules/attributes/services/selectors';
import {ChangeRequestsController} from 'src/modules/change-requests/change-requests.controller';
import {clearChangeRequestsFilters} from 'src/modules/change-requests/services/filters/actions';
import {getFilteredChangeRequestsLength} from 'src/modules/change-requests/services/selectors';
import {EventsController} from 'src/modules/events/events.controller';
import {clearEventsFilters} from 'src/modules/events/services/filters/actions';
import {getFilteredEventsLength} from 'src/modules/events/services/selectors';
import {TabsPanelView} from 'src/modules/tabs-panel/tabs-panel.view';
import {TabCaption, TabId, TabItem} from 'src/modules/tabs-panel/typedefs';
import {getUniqueAttributeTitleCount} from 'src/openapi/apis/AttributeControllerApi';
import {getChangeRequestCount} from 'src/openapi/apis/ChangeRequestControllerApi';
import {getEventCount} from 'src/openapi/apis/EventControllerApi';
import {getCurrentUser} from 'src/openapi/apis/UserControllerApi';
import {useAppDispatch, useAppSelector} from 'src/store';

export const TabsPanelController = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const eventsCount = useAppSelector(getFilteredEventsLength);
    const attributesCount = useAppSelector(getFilteredAttributesLength);
    const changeRequestsCount = useAppSelector(getFilteredChangeRequestsLength);
    const user = useAppSelector(getUser);
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(TabId.EVENTS);
    const [onlyMyFilter, setOnlyMyFilter] = useState(false);
    const [showOnlyMyFilter, setShowOnlyMyFilter] = useState(false);
    const [isCopyLinkDisabled, setIsCopyLinkDisabled] = useState(true);
    const tabs: TabItem[] = useMemo(() => [
        {
            caption: TabCaption.EVENTS,
            tabId: TabId.EVENTS,
            count: eventsCount || 0,
        },
        {
            caption: TabCaption.ATTRIBUTES,
            tabId: TabId.ATTRIBUTES,
            count: attributesCount || 0,
        },
        {
            caption: TabCaption.CHANGE_REQUESTS,
            tabId: TabId.CHANGE_REQUESTS,
            count: changeRequestsCount || 0,
        },
    ], [attributesCount, eventsCount, changeRequestsCount]);

    useEffect(() => {
        if (window.location.href.length >= URL_LENGTH_LIMIT) {
            setIsCopyLinkDisabled(true);
            return;
        }

        setIsCopyLinkDisabled(false);
    }, [searchParams]);

    const onChangeTab = (value: string) => {
        dispatch(clearEventsFilters());
        dispatch(clearAttributesFilters());
        dispatch(clearChangeRequestsFilters());
        navigate(value);
    };

    const onCheckboxChange = (data: {event: React.SyntheticEvent<HTMLInputElement>}) => {
        setOnlyMyFilter(data.event.currentTarget.checked);
    };

    useEffect(() => {
        if (!changeRequestsCount) {
            dispatch(getChangeRequestCount());
        }
        if (!attributesCount) {
            dispatch(getUniqueAttributeTitleCount());
        }
        if (!eventsCount) {
            dispatch(getEventCount());
        }
        if (!user) {
            dispatch(getCurrentUser());
        }
    }, [attributesCount, changeRequestsCount, dispatch, eventsCount, user]);

    useEffect(() => {
        if (location.pathname.includes(TabId.EVENTS)) {
            setActiveTab(TabId.EVENTS);
        }

        if (location.pathname.includes(TabId.ATTRIBUTES)) {
            setActiveTab(TabId.ATTRIBUTES);
        }

        if (location.pathname.includes(TabId.CHANGE_REQUESTS)) {
            setActiveTab(TabId.CHANGE_REQUESTS);
        }
    }, [location]);

    return (
        <>
            <TabsPanelView {...{
                tabs,
                activeTab,
                onlyMyFilter,
                showOnlyMyFilter,
                isCopyLinkDisabled,
                onChangeTab,
                onCheckboxChange,
            }}
            />

            <Routes>
                <Route path={`${TabId.EVENTS}`} element={<EventsController/>}/>
                <Route path={`${TabId.ATTRIBUTES}`} element={<AttributesController/>}/>
                <Route
                    path={`${TabId.CHANGE_REQUESTS}`}
                    element={(
                        <ChangeRequestsController
                            onlyMyFilter={onlyMyFilter}
                            setOnlyMyFilter={setOnlyMyFilter}
                            setShowOnlyMyFilter={setShowOnlyMyFilter}
                        />
                    )}
                />
                <Route index={true} element={<Navigate to={`${TabId.EVENTS}`}/>}/>
                <Route path="*" element={<Navigate to="/404"/>}/>
            </Routes>
        </>
    );
};
